import { sentryModel } from '@/shared/model';

import type { PassengerWithDocType } from '../types';
import { isSamePersistedPassenger } from './identity';

const LOCAL_PASSENGERS_STORAGE_KEY = 'free-avia:local-saved-passengers';

const readRawLocalPassengers = (): PassengerWithDocType[] => {
  try {
    const raw = sessionStorage.getItem(LOCAL_PASSENGERS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    sentryModel.captureException(
      sentryModel.getReadableError(
        'ERR_FAILED_TO_PARSE_LOCAL_PASSENGERS',
        e as Error,
      ),
    );
    return [];
  }
};

const writeRawLocalPassengers = (passengers: PassengerWithDocType[]) => {
  sessionStorage.setItem(
    LOCAL_PASSENGERS_STORAGE_KEY,
    JSON.stringify(passengers),
  );
};

/**
 * Insert or update a passenger in the local (sessionStorage) list. Matches by
 * id first, then by non-empty document number — so a saved-passenger created
 * with a temp uuid that later receives a backend id stays a single row.
 */
const upsertLocalPassenger = (passenger: PassengerWithDocType) => {
  try {
    const passengers = readRawLocalPassengers();
    const existingIdx = passengers.findIndex(pax =>
      isSamePersistedPassenger(pax, passenger),
    );
    if (existingIdx >= 0) {
      passengers[existingIdx] = passenger;
    } else {
      passengers.push(passenger);
    }
    writeRawLocalPassengers(passengers);
  } catch (error) {
    sentryModel.captureException(
      sentryModel.getReadableError(
        'ERR_FAILED_TO_SAVE_LOCAL_PASSENGER',
        error as Error,
      ),
    );
  }
};

const clearLocalPassengers = () => {
  sessionStorage.removeItem(LOCAL_PASSENGERS_STORAGE_KEY);
};

/**
 * Single surface for `sessionStorage`-backed local passengers. The key is
 * private to this module — callers should not hand-roll `sessionStorage.*`
 * for these passengers.
 */
export const localPassengersStorage = {
  read: readRawLocalPassengers,
  write: writeRawLocalPassengers,
  upsert: upsertLocalPassenger,
  clear: clearLocalPassengers,
};
